import {
  BarChart3,
  FileEdit,
  FileText,
  Home,
  Image,
  Layers,
  LayoutDashboard,
  Megaphone,
  MessageSquare,
  Newspaper,
  Plug,
  Rss,
  Settings,
  ShieldCheck,
  Tag,
  Tags,
  User,
  Users,
  Webhook,
  Wrench,
  Youtube,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import type { PermissionCode, SessionUser } from '@news-cms/shared';

export type IconType = ComponentType<SVGProps<SVGSVGElement>>;

export interface NavItem {
  to?: string;
  label: string;
  icon: IconType;
  /** Synonyms used by the command palette fuzzy filter. */
  keywords?: string;
  disabled?: boolean;
  /**
   * Permissions that gate visibility of this nav entry. Item is shown when
   * the user has AT LEAST ONE of the codes (logical OR). Undefined = always
   * visible (Dashboard, Account, the posts list anyone admin can browse).
   */
  requires?: PermissionCode[];
  /**
   * Role slugs that gate visibility — orthogonal to `requires`. Item is
   * shown when the user's role slug is in this list. Used for super_admin-
   * only surfaces like Tools where the gate is "who you are" not "what
   * perm you hold".
   */
  requiresRole?: string[];
  /**
   * Feature-flag gate via a settings table entry. Item is hidden ONLY when
   * the settings map has the key AND its value strictly equals the
   * configured value. Fail-open: if settings are unavailable (loading,
   * 403 for non-settings-admins, key missing on a fresh install), the
   * item stays visible — losing a nav entry to a transient state is
   * worse than a one-frame flicker.
   */
  hiddenWhenSetting?: { key: string; equals: unknown };
  /**
   * Submenu children. When set, this item renders as a collapsible group
   * header (no `to`) and its children render indented below. Children
   * inherit no gates from the parent — each child still declares its
   * own `requires` / `requiresRole`, but in practice the parent's gate
   * is duplicated on every child so the URL is unreachable when the
   * sidebar entry is hidden. `navItemsFor` drops a parent that has all
   * children filtered out.
   */
  children?: NavItem[];
}

/**
 * Single source of truth for the admin nav. Sidebar renders these directly;
 * command palette filters this list by keyword. Keep order matching the
 * dominant user mental model (most-frequent first).
 *
 * `requires` mirrors the backend permission middleware so a user only sees
 * surfaces they can actually use — backend still enforces, this is just UX.
 */
export const ACTIVE_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: BarChart3, keywords: 'home หน้าแรก stats สถิติ' },
  {
    to: '/homepage',
    label: 'หน้าแรก (เว็บ public)',
    icon: Home,
    keywords: 'homepage layout curation slot featured hot lead',
    requires: ['settings.update'],
  },
  {
    label: 'Pages Manager',
    icon: LayoutDashboard,
    keywords: 'pages manager group content layout templates promos',
    // Parent gate intentionally absent — each child declares its own gate;
    // we want /promos to appear for `promos.manage` (editor) while keeping
    // /pages and /group-contents super_admin-only.
    children: [
      {
        to: '/pages',
        label: 'Pages',
        icon: FileText,
        keywords: 'pages landing public หน้า surface',
        requiresRole: ['super_admin'],
      },
      {
        to: '/group-contents',
        label: 'Group Content',
        icon: Layers,
        keywords: 'group content block reusable section slot บล็อค',
        requiresRole: ['super_admin'],
      },
      {
        to: '/promos',
        label: 'ข่าวประชาสัมพันธ์',
        icon: Megaphone,
        keywords: 'promos pr override slot ข่าวประชาสัมพันธ์ ประชาสัมพันธ์ ทับ',
        requires: ['promos.manage'],
      },
    ],
  },
  {
    to: '/feed-sources',
    label: 'Feed Inbox',
    icon: Rss,
    keywords: 'feed rss atom inbox aggregator mgronline manager',
    requires: ['feed.manage'],
  },
  {
    to: '/videos',
    label: 'วิดีโอ YouTube',
    icon: Youtube,
    keywords: 'youtube videos playlist วิดีโอ คลิป',
    requires: ['feed.manage'],
  },
  { to: '/posts', label: 'บทความข่าว', icon: Newspaper, keywords: 'posts list ข่าว' },
  {
    to: '/posts/new',
    label: 'เขียนข่าวใหม่',
    icon: FileEdit,
    keywords: 'create new post compose สร้าง',
    requires: ['post.create'],
  },
  {
    to: '/categories',
    label: 'หมวดหมู่',
    icon: Tags,
    keywords: 'categories taxonomy หมวด',
    requires: ['category.manage'],
  },
  {
    to: '/tags',
    label: 'แท็ก',
    icon: Tag,
    keywords: 'tags taxonomy',
    requires: ['tag.manage'],
  },
  {
    to: '/users',
    label: 'ผู้ใช้งาน',
    icon: Users,
    keywords: 'users people accounts',
    requires: ['user.create', 'user.update', 'user.delete', 'user.invite'],
  },
  {
    to: '/roles',
    label: 'สิทธิ์ Role',
    icon: ShieldCheck,
    keywords: 'roles permissions rbac matrix',
    requires: ['role.manage'],
  },
  {
    to: '/media',
    label: 'Media Library',
    icon: Image,
    keywords: 'media images files uploads',
    requires: ['media.upload', 'media.update', 'media.delete'],
  },
  {
    to: '/comments',
    label: 'คอมเมนต์',
    icon: MessageSquare,
    keywords: 'comments moderation pending approve reject spam',
    requires: ['comment.moderate', 'comment.delete'],
    // Hide when the global comments feature is off — no point showing a
    // moderation queue for a system that doesn't accept new comments.
    hiddenWhenSetting: { key: 'comments.enabled', equals: false },
  },
  {
    to: '/audit',
    label: 'Audit Log',
    icon: FileText,
    keywords: 'audit log history',
    requires: ['audit.read'],
  },
  {
    to: '/webhooks',
    label: 'Webhooks',
    icon: Webhook,
    keywords: 'webhooks api integration notify subscribe events',
    requires: ['webhook.manage'],
  },
  {
    to: '/settings',
    label: 'ตั้งค่า',
    icon: Settings,
    keywords: 'settings preferences config',
    requires: ['settings.update'],
  },
  {
    to: '/tools',
    label: 'Tools',
    icon: Wrench,
    keywords: 'tools backfill migrate sync maintenance ops admin',
    requiresRole: ['super_admin'],
  },
  { to: '/account', label: 'บัญชีของฉัน', icon: User, keywords: 'account profile me 2fa' },
];

export const PLACEHOLDER_NAV: NavItem[] = [{ label: 'Integrations', icon: Plug, disabled: true }];

/**
 * Returns the items the user is allowed to see. Items with no `requires`
 * are always returned. Items with `requires` need at least one matching
 * permission on the user. Items with `hiddenWhenSetting` are hidden ONLY
 * when `settings` is provided AND the key matches the configured value
 * (fail-open on unavailable settings).
 */
export function navItemsFor(
  user: SessionUser | null,
  items: NavItem[] = ACTIVE_NAV,
  settings?: Map<string, unknown>,
): NavItem[] {
  function isAllowed(i: NavItem): boolean {
    if (!user) return !i.requires && !i.requiresRole;
    if (i.requiresRole && !i.requiresRole.includes(user.role.slug)) return false;
    if (i.requires && i.requires.length > 0) {
      const userPerms = new Set(user.permissions);
      if (!i.requires.some((p) => userPerms.has(p))) return false;
    }
    if (i.hiddenWhenSetting && settings && settings.has(i.hiddenWhenSetting.key)) {
      if (settings.get(i.hiddenWhenSetting.key) === i.hiddenWhenSetting.equals) return false;
    }
    return true;
  }
  const out: NavItem[] = [];
  for (const item of items) {
    if (!isAllowed(item)) continue;
    if (item.children) {
      // Recursively filter children; drop the parent entirely if no child
      // survives — an empty group header is just visual noise.
      const visibleChildren = navItemsFor(user, item.children, settings);
      if (visibleChildren.length === 0) continue;
      out.push({ ...item, children: visibleChildren });
    } else {
      out.push(item);
    }
  }
  return out;
}

/** Flatten a nav tree into leaf items only (entries with a `to`). Parent
 *  groups are replaced by their (already-filtered) children. Used by the
 *  command palette so submenu entries are searchable as if they were top-
 *  level. Caller should pass the already-filtered tree from `navItemsFor`. */
export function flattenNavItems(items: NavItem[]): NavItem[] {
  const out: NavItem[] = [];
  for (const item of items) {
    if (item.children) {
      out.push(...flattenNavItems(item.children));
    } else {
      out.push(item);
    }
  }
  return out;
}
